import { useTranslation } from "react-i18next";
import Currency from "components/format/currency/currency";
import SendStatusBadge from "features/invoice-send/send-status-badge";
import { formatPrettyDate } from "utils/helperFunctions";
import type { CustomerErpInvoice } from "types/CustomerLedger";

type Props = {
  invoices: CustomerErpInvoice[];
  onOpen: (invoiceId: string) => void;
};

/**
 * Invoices issued in the ERP that the accounting mirror does not hold yet, so
 * the ledger below cannot show them. Listed on their own and never added to
 * the ledger's totals.
 */
export default function ErpInvoicesSection({ invoices, onOpen }: Props) {
  const { t } = useTranslation();
  if (invoices.length === 0) return null;

  return (
    <section
      className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
      data-testid="erp-invoices-section"
    >
      <h3 className="border-b border-gray-100 px-3 py-2 text-sm font-semibold text-gray-900">
        {t("settlements_erp_invoices_title", {
          defaultValue: "Issued ERP invoices (not in accounting yet)",
        })}
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500">
              <th className="px-3 py-2">{t("invoice_number", { defaultValue: "Invoice number" })}</th>
              <th className="px-3 py-2">{t("ledger_col_date", { defaultValue: "Date" })}</th>
              <th className="px-3 py-2">{t("ledger_col_due_date", { defaultValue: "Due" })}</th>
              <th className="px-3 py-2 text-right">
                {t("totalWithVat", { defaultValue: "Total with VAT" })}
              </th>
              <th className="px-3 py-2">{t("invoice_send_column", { defaultValue: "Emailing" })}</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((invoice) => (
              <tr key={invoice.id} className="border-b border-gray-100 last:border-b-0">
                <td className="whitespace-nowrap px-3 py-2">
                  <button
                    type="button"
                    className="font-medium text-indigo-600 hover:underline"
                    onClick={() => onOpen(invoice.id)}
                  >
                    {invoice.number || "-"}
                  </button>
                </td>
                <td className="whitespace-nowrap px-3 py-2">{formatPrettyDate(invoice.date)}</td>
                <td className="whitespace-nowrap px-3 py-2">
                  {invoice.dueDate ? formatPrettyDate(invoice.dueDate) : "-"}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right">
                  <Currency value={invoice.grossEur} isSymbol isDecimal />
                </td>
                <td className="px-3 py-2">
                  <SendStatusBadge send={invoice.send} showDetails />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
